/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T} RefsType
 */

/**
 * Base class that powers our custom web components.
 *
 * Manages references to child elements with `ref` attributes and sets up mutation observers to keep
 * the refs updated when the DOM changes. Also handles declarative event listeners using.
 *
 * @template {Refs} [T=Refs]
 * @extends {HTMLElement}
 */
class Component extends HTMLElement {
  get [Symbol.toStringTag]() {
    return this.constructor.name
  }
  toString() {
    return `[${this[Symbol.toStringTag]} ${this.id || 'Component'}]`
  }
  /**
   * Logs a message to the console with the component's tag name and any additional arguments.
   *
   * @param {...unknown} args - The arguments to log.
   */
  log(...args) {
    console.log(this.toString(), ...args);
  }
    /**
   * An object holding references to child elements with `ref` attributes.
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

    /**
   * Called when the element is connected to the document's DOM.
   *
   * Initializes event listeners and refs.
   */
  connectedCallback() {
    this.#updateRefs();
  }
  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    this.#updateRefs();
  }
  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  #updateRefs() {
    const refs = /** @type any */ ({});
    const elements = new Set(this.querySelectorAll('[ref]'));

    for (const ref of elements) {
      const refName = ref.getAttribute('ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];

        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }

    this.refs = /** @type {RefsType<T>} */ (refs);
  }
}

/**
 * A custom element that renders a list of all blog articles.
 *
 * @typedef {Object} IngredientsListRefs
 * @property {HTMLDialogElement} ingredientsDialog
 * @property {HTMLButtonElement} showAllButton
 *
 * @extends {Component<IngredientsListRefs>}
 */
class IngredientsList extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.log('connectedCallback', this.refs)
    this.#registerListeners();
  }
  #registerListeners() {
    this.refs.showAllButton.addEventListener('click', () => {
      this.log('showAllButton clicked');
      this.refs.ingredientsDialog.showModal();
    });
    this.refs.ingredientsDialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.log('Escape key pressed, closing ingredientsDialog');
        this.refs.ingredientsDialog.close();
      }
    })
    this.refs.ingredientsDialog.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        this.log('ingredientsDialog closed');
        this.refs.ingredientsDialog.close();
      }
    });
  }
}

if (!customElements.get('ingredients-list')) {
  customElements.define('ingredients-list', IngredientsList);
}
